import { Injectable } from '@angular/core';
import { Subject, BehaviorSubject } from 'rxjs';
import { FormGroup, AbstractControl, FormArray } from '@angular/forms';
import { EnumService } from './enum.service';
import { UtilService } from './util.service';

export interface SummaryItem {
  title: string;
  section: string;
  question: string;
  answer: string;
  popupData: any;
  formValues: any;
  completed: boolean;
  sectionId: string;
  questionId: string;
  isSelected?: boolean;
}

interface ActivityEvent {
  questionId: number;
  answer: any;
  section: any;
  userTriggered?: boolean;
  selectedAnswerIds?: number[];
}

@Injectable({
  providedIn: 'root'
})
export class ActivityService {
  private activityTrigger = new Subject<ActivityEvent>();
  public summaryItems: SummaryItem[] = [];
  
  activityTriggered$ = this.activityTrigger.asObservable();

  private summaryItems$ = new BehaviorSubject<SummaryItem[]>([]);
  summaryItemsObservable$ = this.summaryItems$.asObservable();

  private deselectAnswerSubject = new Subject<any>();
  deselectAnswer$ = this.deselectAnswerSubject.asObservable();

  public isProgrammaticDeselect = false;

  private previousHiddenQuestions: any[] = [];
  private previousHiddenSections: any[] = [];

  private isReconstructing = false;

  EnumService = EnumService;

  triggerActivity(event: any) {
    console.log('triggerActivity called with', event);
    this.activityTrigger.next(event);
  }

  addOrUpdateSummary(item: SummaryItem) {
    item.isSelected = true;
    const itemLogicId = item.popupData?.questionLogic?.questionLogicId;
    const index = this.summaryItems.findIndex(
      i => i.questionId === item.questionId && i.sectionId === item.sectionId &&
        (itemLogicId != null
          ? i.popupData?.questionLogic?.questionLogicId === itemLogicId
          : i.answer === item.answer)
    );

    if (index > -1) {
      this.summaryItems[index] = item;
    } else {
      this.summaryItems.push(item);
    }

    console.log('Updated activitySummaryList:', this.summaryItems);

    this.summaryItems$.next([...this.summaryItems]);
  }

  getSummaryItems(): SummaryItem[] {
    return [...this.summaryItems];
  }

  removeSummaryItemByData(section: string, question: string, answer: string, sectionId?: string, questionId?: string) {
    // const current = this.summaryItems$.getValue();
    // const updated = current.filter(item =>
    //   !(item.section === section && item.question === question && item.answer === answer)
    // );
    // this.summaryItems$.next(updated);
    const removedItem = this.summaryItems.find(item =>
      item.section === section && item.question === question && item.answer === answer
    );
    this.summaryItems = this.summaryItems.filter(item =>
      !(
        item.section === section &&
        item.question === question &&
        item.answer === answer &&
        (sectionId ? item.sectionId === sectionId : true) &&
        (questionId ? item.questionId === questionId : true)
      )
    );
    this.summaryItems$.next([...this.summaryItems]); // push a fresh copy
  }

  // Activities are created once per logic line, so a summary item is identified by its logic.
  findSummaryItemForLogic(sectionId: any, questionId: any, questionLogicId: number): SummaryItem | undefined {
    return this.summaryItems.find(item =>
      item.sectionId === sectionId &&
      item.questionId === questionId &&
      item.popupData?.questionLogic?.questionLogicId === questionLogicId
    );
  }

  removeSummaryItemForLogic(sectionId: any, questionId: any, questionLogicId: number) {
    this.summaryItems = this.summaryItems.filter(item =>
      !(
        item.sectionId === sectionId &&
        item.questionId === questionId &&
        item.popupData?.questionLogic?.questionLogicId === questionLogicId
      )
    );
    this.summaryItems$.next([...this.summaryItems]);
  }

  deselectAnswer(answer: any) {
    this.deselectAnswerSubject.next(answer);
  }

  clearAnswer(answerRef: number, formGroup: FormGroup, formBuilderDetail: any) {
    for (const section of formBuilderDetail.sections) {
      for (const question of section.questions) {
        const found = this.clearAnswerSelection(question, answerRef, formGroup);
        if (!found) continue; // skip unrelated questions

        console.log('Found answer to clear in question:', question.questionId, 'answer type:', question.selectedAnswerTypeId);

        const ctrl = this.findControl(formGroup, question, answerRef);
        console.log('ctrl', ctrl);
        this.updateControlValue(ctrl, question, answerRef);

        // Only clear the matching answer for SingleChoice / MultipleChoice
        if (question.selectedAnswerTypeId === this.EnumService.CustomAnswerType.SingleChoiceSet) {
          const single = question.answerChoiceAttributes.find(a => a.answerChoiceAttributeId === answerRef);
          if (single) single.isSelected = false;
        } else if (question.selectedAnswerTypeId === this.EnumService.CustomAnswerType.MultipleChoiceSet) {
          const multi = question.answerChoiceAttributes.find(a => a.answerChoiceAttributeId === answerRef);
          if (multi) multi.isChecked = false;
        } else if (question.selectedAnswerTypeId === this.EnumService.CustomAnswerType.ConfirmationBox) {
          question.answer = null;
          ctrl?.setValue(false, { emitEvent: false });
        }

        return;
      }
    }
  }

  private clearAnswerSelection(question: any, answerRef: number, formGroup: FormGroup): boolean {
    let answer: any;

    // Determine if this question owns the answerRef
    if (question.selectedAnswerTypeId === this.EnumService.CustomAnswerType.ConfirmationBox) {
      if (question.questionId !== answerRef) return false; // <-- important
      answer = { isChecked: answerRef };
    } else {
      answer = question.answerChoiceAttributes?.find(
        (a: any) => a.answerChoiceAttributeId === answerRef
      );
      if (!answer) return false; // <-- skip unrelated questions
    }

    // Only reset the matching answer
    if (question.selectedAnswerTypeId === this.EnumService.CustomAnswerType.MultipleChoiceSet) {
      answer.isChecked = false;
    } else if (question.selectedAnswerTypeId === this.EnumService.CustomAnswerType.ConfirmationBox) {
      const ctrl = formGroup.get(question.questionId?.toString());
      if (ctrl) ctrl.setValue(false, { emitEvent: false });
    } else {
      answer.isSelected = false;
    }

    if (question.answer?.answerChoiceAttributeId === answerRef) {
      question.answer = null;
    }

    if (question.selectedAnswerId === answerRef) {
      question.selectedAnswerId = null;
    }

    return true;
  }

  // private findControl(formGroup: FormGroup, question: any, answerRef: number): AbstractControl | null {
  //   const possibleKeys = [
  //     question.questionId?.toString?.(),
  //     question.elementId?.toString?.(),
  //     question.questionUniqueName?.toString?.(),
  //     `q_${question.questionId}`,
  //   ].filter(Boolean);

  //   for (const key of possibleKeys) {
  //     if (formGroup.contains(key)) {
  //       return formGroup.get(key);
  //     }
  //   }

  //   //Fallback: scan all controls
  //   for (const [key, control] of Object.entries(formGroup.controls || {})) {
  //     const val = (control as AbstractControl).value;
  //     if (Array.isArray(val) && val.includes(answerRef)) return control as AbstractControl;
  //     if (val === answerRef) return control as AbstractControl;
  //   }

  //   return null;
  // }

  // private findControl(
  //   formGroup: FormGroup,
  //   question: any,
  //   answerRef: number
  // ): AbstractControl | null {

  //   function searchControl(control: AbstractControl | null, target: number): AbstractControl | null {
  //     if (!control) return null;

  //     if (control instanceof FormGroup) {
  //       for (const key of Object.keys(control.controls)) {
  //         const child = control.get(key);

  //         // Match key ending with answerRef (for multi-choice)
  //         if (key.endsWith(target.toString())) return child;

  //         // Recurse
  //         const found = searchControl(child, target);
  //         if (found) return found;
  //       }
  //     } else if (control instanceof FormArray) {
  //       for (let i = 0; i < control.length; i++) {
  //         const found = searchControl(control.at(i), target);
  //         if (found) return found;
  //       }
  //     } else {
  //       // If this is a single FormControl with value = answerRef
  //       const val = control.value;
  //       if (val === target || (typeof val === 'boolean' && val === true)) return control;
  //     }

  //     return null;
  //   }

  //   return searchControl(formGroup, answerRef);
  // }

  private findControl(
    formGroup: FormGroup,
    question: any,
    answerRef: number
  ): AbstractControl | null {

    if (question.selectedAnswerTypeId === this.EnumService.CustomAnswerType.ConfirmationBox) {
      for (const key of Object.keys(formGroup.controls)) {
        if (key.endsWith(`_${question.questionId}`)) {
          console.log('Found confirmation box control by pattern match:', key);
          return formGroup.get(key);
        }
      }
      
      // Fallback to direct lookup
      const possibleKeys = [
        `FormControl_${question.sectionId}_${question.questionId}`,
        `FormControl_${question.parentQuestionID || question.questionId}_${question.questionId}`,
        question.questionId.toString()
      ];
      
      for (const key of possibleKeys) {
        const ctrl = formGroup.get(key);
        if (ctrl) {
          console.log('Found confirmation box control by key:', key);
          return ctrl;
        }
      }
    }

    // For single choice: the control name is FormControl_{parentQuestionID}_{questionId}
    // and its VALUE is the answerRef
    if (question.selectedAnswerTypeId === this.EnumService.CustomAnswerType.SingleChoiceSet) {
      const singleChoiceKey = `FormControl_${question.parentQuestionID || question.questionId}_${question.questionId}`;
      const ctrl = formGroup.get(singleChoiceKey);
      if (ctrl && ctrl.value === answerRef) {
        console.log('Found single choice control by key:', singleChoiceKey);
        return ctrl;
      }
    }

    // For multiple choice: the control name includes the answerRef at the end
    // Try direct lookup with common patterns
    const possibleKeys = [
      `FormControl_${question.questionId}_${answerRef}`,
      `FormControl_${question.parentQuestionID || question.questionId}_${question.questionId}_SubFormControl_${answerRef}`,
      answerRef.toString()
    ];

    for (const key of possibleKeys) {
      const ctrl = formGroup.get(key);
      if (ctrl) {
        console.log('Found control by direct key:', key);
        return ctrl;
      }
  }

  // Recursive search with strict matching
  function searchControl(control: AbstractControl | null, target: number, questionType: number): AbstractControl | null {
    console.log('control', control);
    if (!control) return null;

    if (control instanceof FormGroup) {
      for (const key of Object.keys(control.controls)) {
        const child = control.get(key);

        // For multiple choice: match keys ending with _answerRef
        if (questionType === EnumService.CustomAnswerType.MultipleChoiceSet) {
          const endsWithTarget = key.endsWith(`_${target}`) || key === target.toString();
          
          if (endsWithTarget) {
            console.log('Found multi-choice control by key match:', key, 'for target:', target);
            return child;
          }
        }

        // For single choice: match by value
        if (questionType === EnumService.CustomAnswerType.SingleChoiceSet) {
          if (child && !(child instanceof FormGroup) && !(child instanceof FormArray) && child.value === target) {
            console.log('Found single-choice control by value match:', key, 'for target:', target);
            return child;
          }
        }

        // Recurse into nested FormGroups
        const found = searchControl(child, target, questionType);
        if (found) return found;
      }
    } else if (control instanceof FormArray) {
      for (let i = 0; i < control.length; i++) {
        const found = searchControl(control.at(i), target, questionType);
        if (found) return found;
      }
    }

    return null;
  }

  const result = searchControl(formGroup, answerRef, question.selectedAnswerTypeId);
  
  if (!result) {
    console.warn('Could not find control for answerRef:', answerRef, 'questionType:', question.selectedAnswerTypeId);
  }
  
  return result;
}



  private updateControlValue(ctrl: AbstractControl | null, question: any, answerRef: number) {
    if (!ctrl) return;

    console.log('answer type:', question.selectedAnswerTypeId, 'expected multi:', this.EnumService.CustomAnswerType.MultipleChoiceSet);

    if (question.selectedAnswerTypeId === this.EnumService.CustomAnswerType.SingleChoiceSet) {
      ctrl?.setValue(null, { emitEvent: false });
    } else if (question.selectedAnswerTypeId === this.EnumService.CustomAnswerType.MultipleChoiceSet) {
      console.log('hrrrre 2', ctrl);
      const currentValues = ctrl.value || [];
      console.log('currentValues', currentValues);
      // ctrl.setValue(currentValues.filter((v: any) => v !== answerRef), { emitEvent: false });
      ctrl.setValue(false, { emitEvent: false });
      const ionChecks = document.querySelectorAll('ion-checkbox');
      ionChecks.forEach((checkbox: any) => {
        if (checkbox.value == answerRef) {
          checkbox.checked = false;
        }
      });
    } else if (question.selectedAnswerTypeId === this.EnumService.CustomAnswerType.ConfirmationBox) {
      console.log('yoooollllaaaaa')
      ctrl.setValue(false, { emitEvent: false });
      // const ionCheckbox = document.getElementById(question.questionId.toString()) as HTMLIonCheckboxElement;
      // if (ionCheckbox) ionCheckbox.checked = false;
      const allCheckboxes = document.querySelectorAll('ion-checkbox');
      allCheckboxes.forEach((checkbox: any) => {
        const checkboxId = checkbox.getAttribute('id');
        if (checkboxId && checkboxId.endsWith(`_${question.questionId}`)) {
          console.log('Found checkbox element with id:', checkboxId);
          checkbox.checked = false;
        }
      });
      
      // Fallback: try specific IDs
      const possibleIds = [
        `FormControl_${question.sectionId}_${question.questionId}`,
        question.questionId.toString(),
        `${question.questionId}`
      ];
      
      for (const id of possibleIds) {
        const ionCheckbox = document.getElementById(id) as HTMLIonCheckboxElement;
        if (ionCheckbox) {
          console.log('Found checkbox element with direct id:', id);
          ionCheckbox.checked = false;
          break;
        }
      }
    } else {
      ctrl.setValue(typeof ctrl.value === 'boolean' ? false : null, { emitEvent: false });
    }
  }


  reconstructActivitySummaryFromRelaunch(
    relaunchFormStateData: any,
    formBuilderDetail: any,
    sharedDataService: any,
    activityPopupHandlerService: any
  ) {
    console.log('=== Starting Activity Reconstruction ===');
    console.log('Relaunch data:', relaunchFormStateData);

    this.isReconstructing = true;
    
    if (!relaunchFormStateData?.formAnswerViewModels?.questionAnswers || 
        !relaunchFormStateData?.formBuilderViewModels?.sections) {
      console.log('No question answers or formBuilderViewModels in relaunch data');
      this.isReconstructing = false;
      return;
    }

    const questionAnswers = relaunchFormStateData.formAnswerViewModels.questionAnswers;
    const relaunchSections = relaunchFormStateData.formBuilderViewModels.sections;
    
    console.log('Question answers:', questionAnswers);
    console.log('Relaunch sections:', relaunchSections);
    
    // Clear existing summary items
    this.summaryItems = [];
    let activitiesFound = 0;
    
    // Loop through each answer in relaunch data
    questionAnswers.forEach((savedAnswer: any) => {
      console.log('Processing answer - questionID:', savedAnswer.questionID, 'parentQuestionID:', savedAnswer.parentQuestionID);
      
      // Find the question by matching parentQuestionID
      let foundQuestion: any = null;
      let foundSection: any = null;
      
      for (const section of relaunchSections) {
        const question = section.questions.find((q: any) => {
        //   const parentMatch = q.parentQuestionID !== 0 && savedAnswer.parentQuestionID !== 0 && 
        //                       q.parentQuestionID === savedAnswer.parentQuestionID;
        //   const idMatch = q.questionId === savedAnswer.questionID;
          
        //   if (parentMatch) {
        //     console.log('Matched by parentQuestionID:', q.parentQuestionID);
        //   } else if (idMatch) {
        //     console.log('Matched by questionID:', q.questionId);
        //   }
          
        //   return parentMatch || idMatch;

        if (savedAnswer.parentQuestionID !== 0) {
          const parentMatch = q.parentQuestionID !== 0 && 
                              q.parentQuestionID === savedAnswer.parentQuestionID;
          if (parentMatch) {
            console.log('Matched by parentQuestionID:', q.parentQuestionID);
            return true;
          }
        }
        
        if (savedAnswer.parentQuestionID === 0 || savedAnswer.parentQuestionID === null) {
          const reverseMatch = q.parentQuestionID === savedAnswer.questionID;
          if (reverseMatch) {
            console.log('Matched by reverse lookup - saved questionID:', savedAnswer.questionID, 
                        'matches current parentQuestionID:', q.parentQuestionID);
            return true;
          }
        }
        
        // Fallback to direct ID match
        const idMatch = q.questionId === savedAnswer.questionID;
        if (idMatch) {
          console.log('Matched by questionID:', q.questionId);
        }
        
        return idMatch;

        });
        
        if (question) {
          foundQuestion = question;
          foundSection = section;
          break;
        }
      }
      
      if (!foundQuestion || !foundSection) {
        console.log('Question not found by parentQuestionID:', savedAnswer.parentQuestionID);
        return;
      }
      
      console.log('Found question:', foundQuestion.questionId, 'matched by parentQuestionID:', foundQuestion.parentQuestionID);
      
      // Check if question has activity logics
      const activityLogics = foundQuestion.questionLogics?.filter(
        (logic: any) => logic.questionActionTypeID === 20
      );
      
      if (!activityLogics || activityLogics.length === 0) {
        console.log('No activity logics for this question');
        return;
      }
      
      console.log('Activity logics found:', activityLogics.length);
      
      // Get question text
      const questionText = UtilService.findObj(
        foundQuestion.questionTranslations,
        'questionTranslationLanguageId',
        sharedDataService.getLanguageIdForForm()
      )?.questionTranslationTitle || '';
      
      // Get section name
      const sectionName = UtilService.findObj(
        foundSection.sectionTranslations,
        'sectionTranslationLanguageId',
        sharedDataService.getLanguageIdForForm()
      )?.sectionTranslationTitle || '';
      
      // Handle different answer types
      if (foundQuestion.selectedAnswerTypeId === EnumService.CustomAnswerType.SingleChoiceSet) {
        // Single choice - use singleChoiceValueID
        const selectedAnswerId = savedAnswer.singleChoiceValueID;
        
        if (!selectedAnswerId) {
          console.log('No singleChoiceValueID found');
          return;
        }
        
        console.log('Single choice - selectedAnswerId:', selectedAnswerId);
        
        // Find the answer by parentAnswerChoiceQuestionAttributeId
        // console.log('foundQuestion', foundQuestion);
        // console.log('savedAnswer', savedAnswer);
        // const answerIndex = foundQuestion.answerChoiceAttributes.findIndex(
        //   (a: any) => a.parentAnswerChoiceQuestionAttributeId === selectedAnswerId
        // );

        const answerIndex = foundQuestion.answerChoiceAttributes.findIndex((a: any) => {
          // If parentAnswerChoiceQuestionAttributeId is not 0, try to match by it
          if (a.parentAnswerChoiceQuestionAttributeId !== 0) {
            return a.parentAnswerChoiceQuestionAttributeId === selectedAnswerId;
          }
          // Otherwise, match by answerChoiceAttributeId directly
          return a.answerChoiceAttributeId === selectedAnswerId;
        });
        
        if (answerIndex === -1) {
          console.log('Answer not found by parentAnswerChoiceQuestionAttributeId:', selectedAnswerId);
          return;
        }
        
        const answer = foundQuestion.answerChoiceAttributes[answerIndex];
        const selectedSequence = answerIndex + 1;
        
        // Find matching logic (honours Selected / Not Selected)
        const matchingLogic = activityLogics.find(
          (logic: any) =>
            logic.questionID === foundQuestion.questionId &&
            activityPopupHandlerService.isActivityLogicMet(logic, [selectedSequence])
        );
        
        if (!matchingLogic) {
          console.log('No matching logic for sequence:', selectedSequence);
          return;
        }
        
        console.log('Found matching logic:', matchingLogic.questionLogicId);
        
        // Skip if ALL fields are predefined
        if (activityPopupHandlerService.areAllFieldsPredefined(matchingLogic)) {
          console.log('All fields predefined, skipping:', matchingLogic.activityTitle);
          return;
        }
        
        const answerText = answer?.answerChoiceAttributeHeaders[0]?.answerChoiceAttributeHeaderTitle || '';
        
        // Get duration type ID
        let durationTypeId = 0;
        if (matchingLogic.activityRepeatDurationType) {
          const typeMap: {[key: string]: number} = {
            'Days': 1,
            'Week': 2,
            'Month': 3,
            'Year': 4
          };
          durationTypeId = typeMap[matchingLogic.activityRepeatDurationType] || 0;
        }
        
        // Create summary item
        this.createAndAddSummaryItem(
          foundSection,
          foundQuestion,
          answer,
          matchingLogic,
          sectionName,
          questionText,
          answerText,
          durationTypeId,
          formBuilderDetail.userList,
          formBuilderDetail.companyEntityRoles,
          activityPopupHandlerService.getDeselectRefs(matchingLogic, foundQuestion, answer.answerChoiceAttributeId)
        );
        
        activitiesFound++;
        
      } else if (foundQuestion.selectedAnswerTypeId === EnumService.CustomAnswerType.MultipleChoiceSet) {
        // Multiple choice - parse multipleChoiceValueIDs
        const multipleChoiceIds = savedAnswer.multipleChoiceValueIDs;
        
        if (!multipleChoiceIds) {
          console.log('No multipleChoiceValueIDs found');
          return;
        }

        console.log('savedAnswer', savedAnswer);
        console.log('foundQuestion', foundQuestion);
        
        // Parse comma-separated string to array
        const selectedAnswerIds = multipleChoiceIds.split(',').map((id: string) => parseInt(id.trim()));
        console.log('Multiple choice - selectedAnswerIds:', selectedAnswerIds);

        // Logic is evaluated on all selected answers; several answers meeting the same logic
        // collapse into one summary item (one activity per logic line)
        const selectedSequences = foundQuestion.answerChoiceAttributes
          .map((a: any, index: number) => {
            const answerId = a.parentAnswerChoiceQuestionAttributeId !== 0
              ? a.parentAnswerChoiceQuestionAttributeId
              : a.answerChoiceAttributeId;
            return selectedAnswerIds.includes(answerId) ? index + 1 : 0;
          })
          .filter((sequence: number) => sequence > 0);
        
        selectedAnswerIds.forEach((selectedAnswerId: number) => {
          // Find the answer by parentAnswerChoiceQuestionAttributeId
          // const answerIndex = foundQuestion.answerChoiceAttributes.findIndex(
          //   (a: any) => a.parentAnswerChoiceQuestionAttributeId === selectedAnswerId
          // );

          const answerIndex = foundQuestion.answerChoiceAttributes.findIndex((a: any) => {
            // If parentAnswerChoiceQuestionAttributeId is not 0, try to match by it
            if (a.parentAnswerChoiceQuestionAttributeId !== 0) {
              return a.parentAnswerChoiceQuestionAttributeId === selectedAnswerId;
            }
            // Otherwise, match by answerChoiceAttributeId directly
            return a.answerChoiceAttributeId === selectedAnswerId;
          });
          
          if (answerIndex === -1) {
            console.log('Answer not found:', selectedAnswerId);
            return;
          }
          
          const answer = foundQuestion.answerChoiceAttributes[answerIndex];
          const selectedSequence = answerIndex + 1;
          
          // Find a met logic this answer triggers
          const matchingLogic = activityLogics.find(
            (logic: any) =>
              logic.questionID === foundQuestion.questionId &&
              activityPopupHandlerService.isActivityLogicMet(logic, selectedSequences) &&
              activityPopupHandlerService.getTriggeringAnswerIndex(logic, selectedSequences, selectedSequence) === answerIndex
          );
          
          if (!matchingLogic) {
            console.log('No matching logic for sequence:', selectedSequence);
            return;
          }
          
          // Skip if ALL fields are predefined
          if (activityPopupHandlerService.areAllFieldsPredefined(matchingLogic)) {
            console.log('All fields predefined, skipping');
            return;
          }
          
          const answerText = answer?.answerChoiceAttributeHeaders[0]?.answerChoiceAttributeHeaderTitle || '';
          
          let durationTypeId = 0;
          if (matchingLogic.activityRepeatDurationType) {
            const typeMap: {[key: string]: number} = {
              'Days': 1,
              'Week': 2,
              'Month': 3,
              'Year': 4
            };
            durationTypeId = typeMap[matchingLogic.activityRepeatDurationType] || 0;
          }
          
          this.createAndAddSummaryItem(
            foundSection,
            foundQuestion,
            answer,
            matchingLogic,
            sectionName,
            questionText,
            answerText,
            durationTypeId,
            formBuilderDetail.userList,
            formBuilderDetail.companyEntityRoles,
            activityPopupHandlerService.getDeselectRefs(matchingLogic, foundQuestion, answer.answerChoiceAttributeId)
          );
          
          activitiesFound++;
        });
        
      } else if (foundQuestion.selectedAnswerTypeId === EnumService.CustomAnswerType.ConfirmationBox) {
        // Confirmation box
        if (savedAnswer.isConfirmBoxChecked === true || savedAnswer.isConfirmBoxChecked === 'true') {
          const matchingLogic = activityLogics[0];
          
          if (matchingLogic) {
            if (activityPopupHandlerService.areAllFieldsPredefined(matchingLogic)) {
              console.log('All fields predefined, skipping confirmation box');
              return;
            }
            
            let durationTypeId = 0;
            if (matchingLogic.activityRepeatDurationType) {
              const typeMap: {[key: string]: number} = {
                'Days': 1,
                'Week': 2,
                'Month': 3,
                'Year': 4
              };
              durationTypeId = typeMap[matchingLogic.activityRepeatDurationType] || 0;
            }
            
            this.createAndAddSummaryItem(
              foundSection,
              foundQuestion,
              null,
              matchingLogic,
              sectionName,
              questionText,
              'Confirmed',
              durationTypeId,
              formBuilderDetail.userList,
              formBuilderDetail.companyEntityRoles
            );
            
            activitiesFound++;
          }
        }
      }
    });
    
    console.log('=== Reconstruction Complete ===');
    console.log('Activities found:', activitiesFound);
    console.log('Summary items:', this.summaryItems.length);
    setTimeout(() => {
      this.isReconstructing = false;
      console.log('Reconstruction flag reset');
    }, 500);
  }

  // Helper method to create and add summary item
  private createAndAddSummaryItem(
    section: any,
    question: any,
    answer: any,
    matchingLogic: any,
    sectionName: string,
    questionText: string,
    answerText: string,
    durationTypeId: number,
    userList: any[],
    companyEntityRoles: any[],
    deselectRefs?: any[]
  ) {
    const ENTITY_OWNER_GUID = '6f545e05-72dc-471c-a352-95728c6cd261';
    const enhancedUserList = [
      ...(userList || []),
      {
        userId: ENTITY_OWNER_GUID,
        userName: '= ENTITY OWNER'
      }
    ];
    const popupData = {
      sectionName: sectionName,
      questionText: questionText,
      answerText: answerText,
      questionLogic: matchingLogic,
      userList: enhancedUserList,
      companyEntityRoles: companyEntityRoles,
      sectionId: section.sectionId,
      questionId: question.questionId,
      answerRef: answer ? answer.answerChoiceAttributeId : question.questionId,
      deselectRefs: deselectRefs
    };

    const userIds = matchingLogic.questionLogicAssignees?.selectedUsersAndGroups
      ?.filter((item: any) => item.userId !== null)
      ?.map((u: any) => u.userId) || [];
    
    const roles = matchingLogic.questionLogicAssignees?.selectedEntityRoles?.map((role: any) => ({
      userId: role.entityRoleId,
      userName: role.entityRoleName,
      isRole: true,
      entityRoleId: role.entityRoleId
    })) || [];

    const groups = matchingLogic.questionLogicAssignees?.selectedUsersAndGroups
      ?.filter((item: any) => item.groupId !== null)
      ?.map((item: any) => ({
        userId: item.groupId,
        userName: item.name,
        isGroup: true,
        groupId: item.groupId
      })) || [];
    
    const users = matchingLogic.questionLogicAssignees?.selectedUsersAndGroups
      ?.filter((item: any) => item.userId !== null)
      ?.map((item: any) => ({
        userId: item.userId,
        userName: item.name,
      })) || [];

    let ownerArray = [];

    if (matchingLogic.isOwnerPredefined === true || matchingLogic.isOwnerPredefined === null) {
      // Check if activityOwnerID is valid
      if (matchingLogic.activityOwnerID && 
          matchingLogic.activityOwnerID !== '00000000-0000-0000-0000-000000000000' && 
          matchingLogic.activityOwnerID !== null) {
        ownerArray = [matchingLogic.activityOwnerID];
      }
      // Check if activityEntityRoleOwnerID is valid
      else if (matchingLogic.activityEntityRoleOwnerID && 
              matchingLogic.activityEntityRoleOwnerID !== null && 
              matchingLogic.activityEntityRoleOwnerID !== 0) {
        ownerArray = [matchingLogic.activityEntityRoleOwnerID];
      }
    }
    
    const formValues = {
      activityTitle: matchingLogic.activityTitle || '',
      activityDescription: matchingLogic.activityDescription || '',
      activityExplanation: matchingLogic.activityExplanation || '',
      isHighPriorityActivity: matchingLogic.isHighPriorityActivity || false,
      owner: ownerArray,
      assignees: userIds,
      assigneesDisplay: [...users, ...groups, ...roles],
      dueDate: matchingLogic.dateValue || '',
      expireAfterDuration: matchingLogic.activityRepeatDurationValue || '',
      expireAfterDurationType: durationTypeId
    };
    
    const summaryItem = {
      title: formValues.activityTitle,
      section: sectionName,
      question: questionText,
      answer: answerText,
      sectionId: section.sectionId,
      questionId: question.questionId,
      completed: false,
      isSelected: true,
      popupData: popupData,
      formValues: formValues
    };
    
    this.addOrUpdateSummary(summaryItem);
    console.log('✓ Added activity:', summaryItem.title);
  }

  // checkForHiddenChanges(scoringData: any) {
  //   if (!scoringData.enabled) return;

  //   const currentHiddenQuestions = scoringData.hiddenQuestions || [];
  //   const currentHiddenSections = scoringData.hiddenSections || [];
    
  //   const questionsChanged = JSON.stringify(currentHiddenQuestions) !== JSON.stringify(this.previousHiddenQuestions);
  //   const sectionsChanged = JSON.stringify(currentHiddenSections) !== JSON.stringify(this.previousHiddenSections);

  //   if (questionsChanged || sectionsChanged) {
  //     console.log('Hidden items changed:', {
  //       hiddenQuestions: currentHiddenQuestions,
  //       hiddenSections: currentHiddenSections
  //     });

  //     const itemsToRemove = this.summaryItems.filter(
  //       item => 
  //         currentHiddenQuestions.includes(item.questionId) || 
  //         currentHiddenSections.includes(item.sectionId)
  //     );

  //     itemsToRemove.forEach(item => {
  //       this.removeSummaryItemByData(
  //         item.section,
  //         item.question,
  //         item.answer,
  //         item.sectionId,
  //         item.questionId
  //       );
  //     });

  //     if (itemsToRemove.length > 0) {
  //       console.log(`Removed ${itemsToRemove.length} activities due to hidden questions/sections`);
  //     }

  //     this.previousHiddenQuestions = [...currentHiddenQuestions];
  //     this.previousHiddenSections = [...currentHiddenSections];
  //   }
  // }

  checkForHiddenItemsChange(currentHiddenQuestions: any[], currentHiddenSections: any[]) {
    if (this.isReconstructing) {
      console.log('Skipping hidden items check during reconstruction');
      this.previousHiddenQuestions = [...currentHiddenQuestions];
      this.previousHiddenSections = [...currentHiddenSections];
      return;
    }
    // Find NEWLY hidden questions (were visible before, now hidden)
    const newlyHiddenQuestions = currentHiddenQuestions.filter(
      qId => !this.previousHiddenQuestions.includes(qId)
    );

    // Find NEWLY hidden sections (were visible before, now hidden)
    const newlyHiddenSections = currentHiddenSections.filter(
      sId => !this.previousHiddenSections.includes(sId)
    );

    if (newlyHiddenQuestions.length > 0 || newlyHiddenSections.length > 0) {
      console.log('Newly hidden items:', {
        newlyHiddenQuestions,
        newlyHiddenSections
      });

      // Only remove activities for NEWLY hidden items
      const itemsToRemove = this.summaryItems.filter(
        item => 
          newlyHiddenQuestions.includes(item.questionId) || 
          newlyHiddenSections.includes(item.sectionId)
      );

      itemsToRemove.forEach(item => {
        this.removeSummaryItemByData(
          item.section,
          item.question,
          item.answer,
          item.sectionId,
          item.questionId
        );
      });

      if (itemsToRemove.length > 0) {
        console.log(`Removed ${itemsToRemove.length} activities due to newly hidden questions/sections`);
      }
    }

    // Update the tracking arrays
    this.previousHiddenQuestions = [...currentHiddenQuestions];
    this.previousHiddenSections = [...currentHiddenSections];
  }

  checkForHiddenQuestionsChange(currentHiddenQuestions: any[]) {
    const questionsChanged = JSON.stringify(currentHiddenQuestions) !== JSON.stringify(this.previousHiddenQuestions);

    if (questionsChanged) {
      console.log('Hidden questions changed:', currentHiddenQuestions);

      const itemsToRemove = this.summaryItems.filter(
        item => currentHiddenQuestions.includes(item.questionId)
      );

      itemsToRemove.forEach(item => {
        this.removeSummaryItemByData(
          item.section,
          item.question,
          item.answer,
          item.sectionId,
          item.questionId
        );
      });

      if (itemsToRemove.length > 0) {
        console.log(`Removed ${itemsToRemove.length} activities due to hidden questions`);
      }

      this.previousHiddenQuestions = [...currentHiddenQuestions];
    }
  }

  checkForHiddenSectionsChange(currentHiddenSections: any[]) {
    const sectionsChanged = JSON.stringify(currentHiddenSections) !== JSON.stringify(this.previousHiddenSections);

    if (sectionsChanged) {
      console.log('Hidden sections changed:', currentHiddenSections);

      const itemsToRemove = this.summaryItems.filter(
        item => currentHiddenSections.includes(item.sectionId)
      );

      itemsToRemove.forEach(item => {
        this.removeSummaryItemByData(
          item.section,
          item.question,
          item.answer,
          item.sectionId,
          item.questionId
        );
      });

      if (itemsToRemove.length > 0) {
        console.log(`Removed ${itemsToRemove.length} activities due to hidden sections`);
      }

      this.previousHiddenSections = [...currentHiddenSections];
    }
  }
}